import _extends from "@babel/runtime/helpers/esm/extends";
import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
var _excluded = ["initialFiles", "disabled", "error", "warning", "multiple", "width", "hideFiles", "onBlur", "onFocus", "onChange", "request", "validateBeforeUpload", "onRequestSuccess", "onRequestError", "size", "renderFile"];
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useCallback, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { globalObject, isBrowser } from '@skbkontur/global-object';
import { getAttachedFile } from "../../../internal/FileUploaderControl/fileUtils";
import { cx } from "../../../lib/theming/Emotion";
import { useMemoObject } from "../../../hooks/useMemoObject";
import { FileUploaderControlContext } from "../../../internal/FileUploaderControl/FileUploaderControlContext";
import { useControlLocale } from "../../../internal/FileUploaderControl/hooks/useControlLocale";
import { useUpload } from "../../../internal/FileUploaderControl/hooks/useUpload";
import { useDrop } from "../../../hooks/useDrop";
import { ThemeContext } from "../../../lib/theming/ThemeContext";
import { UploadIcon } from "../../../internal/icons/16px";
import { withFileUploaderControlProvider } from "../../../internal/FileUploaderControl/withFileUploaderControlProvider";
import { keyListener } from "../../../lib/events/keyListener";
import { FileUploaderFile } from "../../../internal/FileUploaderControl/FileUploaderFile/FileUploaderFile";
import { FileUploaderFileList } from "../../../internal/FileUploaderControl/FileUploaderFileList/FileUploaderFileList";
import { CommonWrapper } from "../../../internal/CommonWrapper";
import { FileUploaderFileValidationResult } from "../../../internal/FileUploaderControl/FileUploaderFileValidationResult";
import { useFileUploaderSize } from "../../../internal/FileUploaderControl/hooks/useFileUploaderSize";
import { isTheme2022 } from "../../../lib/theming/ThemeHelpers";
import { forwardRefAndName } from "../../../lib/forwardRefAndName";
import { FocusControlWrapper } from "../../../internal/FocusControlWrapper";
import { UploadIcon as UploadIcon2022 } from "../UploadIcon";
import { globalClasses, jsStyles } from "../FileUploader.styles";

var stopPropagation = function stopPropagation(e) {
  return e.stopPropagation();
};
/**
 * @deprecated use SizeProp
 */


export var FileUploaderDataTids = {
  root: 'FileUploader__root',
  content: 'FileUploader__content',
  link: 'FileUploader__link',
  input: 'FileUploader__input'
};

var defaultRenderFile = function defaultRenderFile(file, fileNode) {
  return fileNode;
};

var _FileUploader = forwardRefAndName('FileUploader', function (props, ref) {
  var _cx;

  var theme = useContext(ThemeContext);

  var _isTheme2022 = isTheme2022(theme);

  var initialFiles = props.initialFiles,
      disabled = props.disabled,
      error = props.error,
      warning = props.warning,
      _props$multiple = props.multiple,
      multiple = _props$multiple === void 0 ? false : _props$multiple,
      _props$width = props.width,
      width = _props$width === void 0 ? theme.fileUploaderWidth : _props$width,
      _props$hideFiles = props.hideFiles,
      hideFiles = _props$hideFiles === void 0 ? false : _props$hideFiles,
      onBlur = props.onBlur,
      onFocus = props.onFocus,
      onChange = props.onChange,
      request = props.request,
      validateBeforeUpload = props.validateBeforeUpload,
      onRequestSuccess = props.onRequestSuccess,
      onRequestError = props.onRequestError,
      _props$size = props.size,
      size = _props$size === void 0 ? 'small' : _props$size,
      _props$renderFile = props.renderFile,
      renderFile = _props$renderFile === void 0 ? defaultRenderFile : _props$renderFile,
      inputProps = _objectWithoutPropertiesLoose(props, _excluded);

  var _useContext = useContext(FileUploaderControlContext),
      files = _useContext.files,
      setFiles = _useContext.setFiles,
      removeFile = _useContext.removeFile,
      reset = _useContext.reset,
      setFileValidationResult = _useContext.setFileValidationResult,
      isMinLengthReached = _useContext.isMinLengthReached;

  var locale = useControlLocale();
  var inputRef = useRef(null);
  var fileDivRef = useRef(null);
  var isAsync = !!request;
  var isSingleMode = !multiple;

  var _useState = useState(true),
      isLinkVisible = _useState[0],
      setIsLinkVisible = _useState[1];

  var upload = useUpload(request, onRequestSuccess, onRequestError);
  var tryValidateAndUpload = useCallback(function (files) {
    files.forEach( /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(file) {
        var validationMessage;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _context.t0 = validateBeforeUpload;

                if (!_context.t0) {
                  _context.next = 5;
                  break;
                }

                _context.next = 4;
                return validateBeforeUpload(file);

              case 4:
                _context.t0 = _context.sent;

              case 5:
                validationMessage = _context.t0;

                if (!validationMessage) {
                  isAsync && upload(file);
                } else {
                  setFileValidationResult(file.id, FileUploaderFileValidationResult.error(validationMessage));
                }

              case 7:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      return function (_x) {
        return _ref.apply(this, arguments);
      };
    }());
  }, [validateBeforeUpload, isAsync, upload, setFileValidationResult]);
  var sizeClassName = useFileUploaderSize(size, {
    small: jsStyles.sizeSmall(theme),
    medium: jsStyles.sizeMedium(theme),
    large: jsStyles.sizeLarge(theme)
  });
  var sizeIconClass = useFileUploaderSize(size, {
    small: jsStyles.iconSmall(theme),
    medium: jsStyles.iconMedium(theme),
    large: jsStyles.iconLarge(theme)
  });
  var contentInnerClass = useFileUploaderSize(size, {
    small: jsStyles.contentInnerSmall(theme),
    medium: jsStyles.contentInnerMedium(theme),
    large: jsStyles.contentInnerLarge(theme)
  });
  /** common part **/

  var handleChange = useCallback(function (newFiles) {
    if (!newFiles || !newFiles.length) {
      return;
    }

    var filesArray = Array.from(newFiles);

    if (isSingleMode) {
      filesArray = [filesArray[0]];
    }

    var attachedFiles = filesArray.map(getAttachedFile);

    if (isSingleMode && attachedFiles.length && files.length) {
      removeFile(files[0].id);
    }

    if (attachedFiles.length) {
      setFiles(attachedFiles);
      tryValidateAndUpload(attachedFiles);
    }
  }, [tryValidateAndUpload, setFiles, isSingleMode, files, removeFile]);
  var handleDrop = useCallback(function (event) {
    if (disabled) {
      return;
    }

    var dataTransfer = event.dataTransfer;
    var files = dataTransfer.files;

    if ((files == null ? void 0 : files.length) > 0) {
      handleChange(files);
      dataTransfer.clearData();
    }
  }, [handleChange, disabled]);

  var _useDrop = useDrop({
    onDrop: handleDrop
  }),
      isDraggable = _useDrop.isDraggable,
      labelRef = _useDrop.ref;

  var _useDrop2 = useDrop(),
      isWindowDraggable = _useDrop2.isDraggable,
      windowRef = _useDrop2.ref;

  if (isBrowser(globalObject)) {
    windowRef.current = globalObject.document;
  }

  var focus = useCallback(function () {
    var _inputRef$current;

    keyListener.isTabPressed = true;
    (_inputRef$current = inputRef.current) == null ? void 0 : _inputRef$current.focus();
  }, []);
  var blur = useCallback(function () {
    var _inputRef$current2;

    (_inputRef$current2 = inputRef.current) == null ? void 0 : _inputRef$current2.blur();
  }, []);
  useImperativeHandle(ref, function () {
    return {
      focus: focus,
      blur: blur,
      reset: reset,
      getRootNode: function getRootNode() {
        return rootNodeRef.current;
      }
    };
  }, [ref, blur, focus, reset]);

  var _useState2 = useState(false),
      focusedByTab = _useState2[0],
      setFocusedByTab = _useState2[1];

  var handleInputChange = function handleInputChange(event) {
    onChange == null ? void 0 : onChange(event);
    handleChange(event.target.files);
  };

  var handleFocus = function handleFocus(e) {
    if (!disabled) {
      // focus event fires before keyDown eventlistener
      // so we should check tabPressed in async way
      globalObject.requestAnimationFrame == null ? void 0 : globalObject.requestAnimationFrame(function () {
        if (keyListener.isTabPressed) {
          setFocusedByTab(true);
        }
      });
      onFocus == null ? void 0 : onFocus(e);
    }
  };

  var handleBlur = function handleBlur(e) {
    setFocusedByTab(false);

    if (!disabled) {
      onBlur == null ? void 0 : onBlur(e);
    }
  };

  var handleRemoveFile = useCallback(function (fileId) {
    var dataTransfer = new DataTransfer();
    files.filter(function (f) {
      return f.id !== fileId;
    }).forEach(function (file) {
      dataTransfer.items.add(file.originalFile);
    });
    inputRef.current && (inputRef.current.files = dataTransfer.files);
  }, []);

  var _useState3 = useState(false),
      hovered = _useState3[0],
      setHovered = _useState3[1];

  var uploadButtonClassNames = cx(jsStyles.uploadButton(theme), sizeClassName, focusedByTab && jsStyles.uploadButtonFocus(theme), disabled && jsStyles.disabled(theme), !disabled && hovered && jsStyles.hovered(theme), !!warning && jsStyles.warning(theme), !!error && jsStyles.error(theme), isDraggable && !disabled && jsStyles.dragOver(theme));
  var canDrop = isWindowDraggable && !disabled;
  var uploadButtonWrapperClassNames = cx(!_isTheme2022 && canDrop && jsStyles.windowDragOver(theme), _isTheme2022 && canDrop && jsStyles.windowDragOver2022(theme));
  var uploadButtonIconClassNames = cx(jsStyles.icon(theme), sizeIconClass, disabled && jsStyles.iconDisabled(theme));
  var hasOneFile = files.length === 1;
  var hasOneFileForSingle = isSingleMode && hasOneFile && !hideFiles;
  var contentClassNames = cx(jsStyles.content(), hasOneFileForSingle && jsStyles.contentWithFiles());
  var linkClassNames = cx(jsStyles.link(theme), !disabled && hovered && jsStyles.linkHovered(theme), disabled && jsStyles.linkDisabled(theme));
  useEffect(function () {
    setIsLinkVisible(hasOneFileForSingle ? !isMinLengthReached : true);
  }, [isMinLengthReached, hasOneFileForSingle]);
  useEffect(function () {
    if (!files || !files.length || !inputRef.current) {
      return;
    }

    var dataTransfer = new DataTransfer();
    files.forEach(function (file) {
      return dataTransfer.items.add(file.originalFile);
    });
    inputRef.current.files = dataTransfer.files;
  }, []);
  var rootNodeRef = useRef(null);
  var iconSizes = {
    small: parseInt(theme.btnIconSizeSmall),
    medium: parseInt(theme.btnIconSizeMedium),
    large: parseInt(theme.btnIconSizeLarge)
  };
  var icon = _isTheme2022 ? /*#__PURE__*/React.createElement(UploadIcon2022, {
    size: iconSizes[size]
  }) : /*#__PURE__*/React.createElement(UploadIcon, null);
  return /*#__PURE__*/React.createElement(CommonWrapper, props, /*#__PURE__*/React.createElement("div", {
    "data-tid": FileUploaderDataTids.root,
    className: jsStyles.root(theme),
    style: useMemoObject({
      width: width
    }),
    ref: rootNodeRef
  }, !hideFiles && !isSingleMode && !!files.length && /*#__PURE__*/React.createElement(FileUploaderFileList, {
    renderFile: renderFile,
    size: size,
    onRemove: handleRemoveFile
  }), /*#__PURE__*/React.createElement("div", {
    className: uploadButtonWrapperClassNames
  }, /*#__PURE__*/React.createElement("label", {
    onMouseEnter: function onMouseEnter() {
      return setHovered(true);
    },
    onMouseLeave: function onMouseLeave() {
      return setHovered(false);
    },
    ref: labelRef,
    className: uploadButtonClassNames
  }, /*#__PURE__*/React.createElement("div", {
    "data-tid": FileUploaderDataTids.content,
    className: cx(contentClassNames, (_cx = {}, _cx[contentInnerClass] = !files.length || !isSingleMode, _cx))
  }, isLinkVisible && /*#__PURE__*/React.createElement("span", {
    "data-tid": FileUploaderDataTids.link,
    className: linkClassNames
  }, hasOneFileForSingle ? locale.choosedFile : locale.chooseFile), isLinkVisible && String.fromCharCode(0xa0)
  /* &nbsp; */
  , /*#__PURE__*/React.createElement("div", {
    className: cx(globalClasses.afterLinkText, hasOneFileForSingle ? jsStyles.afterLinkText_HasFiles(theme) : jsStyles.afterLinkText(theme))
  }, hasOneFileForSingle ? /*#__PURE__*/React.createElement("div", {
    ref: fileDivRef,
    className: jsStyles.singleFile()
  }, renderFile(files[0], /*#__PURE__*/React.createElement(FileUploaderFile, {
    file: files[0],
    size: size,
    onRemove: handleRemoveFile
  }))) : /*#__PURE__*/React.createElement(React.Fragment, null, locale.orDragHere, "\xA0", /*#__PURE__*/React.createElement("div", {
    className: uploadButtonIconClassNames
  }, icon)))), /*#__PURE__*/React.createElement(FocusControlWrapper, {
    onBlurWhenDisabled: function onBlurWhenDisabled() {
      return setFocusedByTab(false);
    }
  }, /*#__PURE__*/React.createElement("input", _extends({}, inputProps, {
    "data-tid": FileUploaderDataTids.input,
    ref: inputRef,
    tabIndex: disabled ? -1 : 0,
    type: "file",
    disabled: disabled,
    multiple: multiple,
    className: jsStyles.visuallyHidden(),
    onClick: stopPropagation,
    onChange: handleInputChange,
    onFocus: handleFocus,
    onBlur: handleBlur
  })))))));
});

export var FileUploader = withFileUploaderControlProvider( /*#__PURE__*/React.memo(_FileUploader));
FileUploader.displayName = 'FileUploader';